import { useParams } from "react-router-dom";
import { getListingConfig } from "@/lib/listingSlugs";
import { extractIdPrefix } from "@/lib/slugify";
import { useTours } from "@/hooks/useTours";
import { slugify } from "@/lib/slugify";
import TourDetail from "./TourDetail";
import Company from "./Company";
import Listing from "./Listing";
import NotFound from "./NotFound";
import { Loader2 } from "lucide-react";

/**
 * Resolves /aruba/:slug to the correct page type:
 * 1. Listing page (category or boat type) — matched by known listing slugs
 * 2. Tour detail — slug ends with 8-char ID prefix
 * 3. Company page — matched by company name slug
 * 4. 404
 */
export default function ArubaResolver() {
  const { slug } = useParams<{ slug: string }>();
  const { data: tours, isLoading } = useTours();

  if (!slug) return <NotFound />;

  // 1. Check listing pages first (known static slugs)
  const listingConfig = getListingConfig(slug);
  if (listingConfig) {
    return <Listing />;
  }

  // Need tours data for tour/company resolution
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // 2. Check if it's a tour (slug ends with 8-char ID prefix)
  const idPrefix = extractIdPrefix(slug);
  if (idPrefix && tours) {
    const tour = tours.find((t) => t.id.startsWith(idPrefix));
    if (tour) {
      return <TourDetail />;
    }
  }

  // 3. Check if it's a company page
  if (tours) {
    const companyNames = [...new Set(tours.filter((t) => t.company_name).map((t) => t.company_name!))];
    const matchedCompany = companyNames.find((name) => slugify(name) === slug);
    if (matchedCompany) {
      return <Company />;
    }
  }

  return <NotFound />;
}
